import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

export const BookingForm = ({ isFreeDayPass = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    company_size: '',
    termsAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
  
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name should contain only letters';
    }
  
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
  
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
  
    if (!formData.company.trim()) {
      newErrors.company = 'Company Name is required';
    }
  
    if (!formData.company_size) {
      newErrors.company_size = 'Company size is required';
    }
  
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
    }
  
    return newErrors;
  };
  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    if (name === 'phone') {
      // Only allow numbers and limit to 10 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, [name]: numericValue });
    } else if (name === 'name') {
      // Remove numeric characters from name input
      const filteredValue = value.replace(/[0-9]/g, '');
      setFormData({ ...formData, [name]: filteredValue });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const getTypeFromPath = (pathname) => {
      if (pathname === '/affordable-coworking-space') return 'Affordability';
      else if (pathname === '/mount-road-chennai') return 'Location';
      else if (pathname === '/virtual-office-chennai') return 'Virtual';
      else if (pathname === '/free-day-pass') return 'freeday';
      else if (pathname === '/meeting-room') return 'meetingroom';
      else return 'general';
    };
    const type = getTypeFromPath(location.pathname);

    try {
      const { termsAccepted, ...payload } = formData;

      await axios.post('https://offisbay.com/getintouch.php', {
        ...payload,
        type,
      });

      navigate(`/thank-you?type=${type.toLowerCase()}`);
    } catch (err) {
      console.error('Error:', err);
      alert('There was a problem submitting your booking. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="book-a-tour" className="bg-[#00000066] backdrop-blur-md rounded-3xl p-6 sm:p-8 w-full">
      <h2 className="text-white text-2xl sm:text-3xl font-bold font-regrade text-center mb-6">
        {location.pathname === '/free-day-pass' ? 'Book a Free Day Pass' : location.pathname === '/meeting-room' ? 'Schedule Now' : 'Book A Tour'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="text-white mb-2 text-sm sm:text-base">
              Name <span className='text-[#c50303]'>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter Your Name"
              className="bg-white text-black p-3 rounded text-sm sm:text-base"
            />
            {errors.name && <span className="text-red-500 text-sm mt-1">{errors.name}</span>}
          </div>

          <div className="flex flex-col">
            <label className="text-white mb-2 text-sm sm:text-base">
              Phone Number <span className='text-[#c50303]'>*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter Your Number"
              maxLength="10"
              className="bg-white text-black p-3 rounded text-sm sm:text-base"
            />
            {errors.phone && <span className="text-red-500 text-sm mt-1">{errors.phone}</span>}
          </div>

          <div className="flex flex-col">
            <label className="text-white mb-2 text-sm sm:text-base">
              Email Id <span className='text-[#c50303]'>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter Your Email"
              className="bg-white text-black p-3 rounded text-sm sm:text-base"
            />
            {errors.email && <span className="text-red-500 text-sm mt-1">{errors.email}</span>}
          </div>

          <div className="flex flex-col">
            <label className="text-white mb-2 text-sm sm:text-base">
              Company Name <span className='text-[#c50303]'>*</span>
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Enter Your Company Name"
              className="bg-white text-black p-3 rounded text-sm sm:text-base"
            />
            {errors.company && (
              <span className="text-red-500 text-sm mt-1">{errors.company}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-white mb-2 text-sm sm:text-base">
            Company Size <span className='text-[#c50303]'>*</span>
          </label>
          <select
            name="company_size"
            value={formData.company_size}
            onChange={handleChange}
            className="bg-white text-black p-3 rounded text-sm sm:text-base"
          >
            <option value="" disabled>Select</option>
            <option value="1-5">1-5</option>
            <option value="6-20">6-20</option>
            <option value="21-50">21-50</option>
            <option value="51-100">51-100</option>
            <option value="100+">100+</option>
          </select>
          {errors.company_size && (
            <span className="text-red-500 text-sm mt-1">{errors.company_size}</span>
          )}
        </div>

        <div className="flex items-start gap-2 mt-4">
          <input
            type="checkbox"
            name="termsAccepted"
            checked={formData.termsAccepted}
            onChange={handleChange}
            className="mt-1"
            id="termsAccepted"
          />
          <label htmlFor="termsAccepted" className="text-white text-xs sm:text-sm">
            I agree to the{' '}
            <a href="/terms-and-conditions" className="text-[#966376] underline">Terms of service</a> and{' '}
            <a href="/privacy-policy" className="text-[#966376] underline">Privacy policy</a> of{' '}
            <span className="font-bold">Cospace</span> company
          </label>
        </div>
        {errors.termsAccepted && <span className="text-red-500 text-sm">{errors.termsAccepted}</span>}

        <button
          type="submit"
          className="w-full bg-[#966376] sans uppercase text-white font-semibold py-3 rounded mt-4 hover:bg-[#7d5262] transition-colors disabled:opacity-50 text-sm sm:text-base"
          disabled={isSubmitting}
        >
          {location.pathname === '/virtual-office-chennai' ? 'GET VIRTUAL OFFICE NOW' : location.pathname === '/free-day-pass' ? 'CONFIRM MY DAY PASS' : location.pathname === '/meeting-room' ? 'Secure Your Space' : 'SECURE YOUR VISIT'}
        </button>
      </form>
    </div>
  );
};
